/**
 * Obligation API service – feature-level data fetching for obligations.
 * Uses shared ApiService; errors propagate to callers (no try/catch here).
 */
import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import {
  assertCanUpdateObligationStatus,
  getAuthUser,
  sanitizeObligationSavePayload,
} from '@/shared/auth/obligationAuth';
import { assertValidObligationStatusTransition } from '@/shared/utils/obligationPhaseAcl';

const OBLIGATION = API_ENDPOINTS.OGP.OBLIGATION;

/**
 * Search obligations (POST /obligation/search). Filteri mogu biti kombinovani.
 * @param {Object} filters
 * @param {string} [filters.action_plan_id]
 * @param {string} [filters.proposed_by_organization_id]
 * @param {string} [filters.proposed_by]
 * @param {string} [filters.id]
 * @returns {Promise<{ obligations: Array }>}
 */
export async function searchObligations(filters = {}) {
  const body = {};
  if (filters.action_plan_id) body.action_plan_id = filters.action_plan_id;
  if (Object.prototype.hasOwnProperty.call(filters, 'proposed_by_organization_id')) {
    body.proposed_by_organization_id = filters.proposed_by_organization_id;
  }
  if (
    filters.proposed_by != null &&
    String(filters.proposed_by).trim() !== '' &&
    !String(filters.proposed_by).includes('@')
  ) {
    body.proposed_by = String(filters.proposed_by).trim();
  }
  if (filters.id) body.id = filters.id;

  const hasActionPlan = !!body.action_plan_id;
  const hasProposedBy = !!body.proposed_by;
  const orgRaw = body.proposed_by_organization_id;
  const hasMeaningfulOrg =
    Object.prototype.hasOwnProperty.call(body, 'proposed_by_organization_id') &&
    orgRaw != null &&
    String(orgRaw).trim() !== '';

  if (!hasActionPlan && !hasProposedBy && !hasMeaningfulOrg) {
    throw new Error('Za pretragu obaveza potreban je action_plan_id, proposed_by_organization_id ili proposed_by');
  }

  const response = await ApiService.post(OBLIGATION.SEARCH, body);
  return response.data;
}

/**
 * Create or update an obligation.
 * @param {Object} obligation
 * @returns {Promise<Object>} Saved obligation (with id, status, etc.) and response object
 */
export async function saveOrUpdateObligation(obligation) {
  const user = await getAuthUser();
  const body = sanitizeObligationSavePayload(obligation, { user });
  const response = await ApiService.post(OBLIGATION.SAVE_OR_UPDATE, body);
  // Vraćamo i response objekat da bismo mogli proveriti status code
  return {
    ...response.data,
    _response: response, // Dodajemo response objekat za proveru status code-a
  };
}

/**
 * Snima ručni redosled obaveza akcionog plana.
 * @param {string|number} actionPlanId - ID akcionog plana.
 * @param {Array<string|number>} obligationIds - ID-jevi obaveza u novom redosledu.
 * @returns {Promise<object>} API odgovor.
 */
export async function reorderObligations(actionPlanId, obligationIds) {
  const response = await ApiService.post(OBLIGATION.REORDER, {
    action_plan_id: actionPlanId,
    obligation_ids: obligationIds,
  });
  return response.data;
}

/** Backend odbija `status_change_comment` duži od ovoga sa 400. */
export const OBLIGATION_STATUS_COMMENT_MAX_LENGTH = 5000;

/**
 * Ažurira status obaveze sa auth i tranzicionom proverom.
 * @param {string|number} obligationId - ID obaveze.
 * @param {string} status - Ciljni status.
 * @param {string} [comment=''] - Opcioni komentar promene statusa (`status_change_comment`).
 * @param {{ currentStatus?: string, actionPlanStatus?: string }} [options] - Kada je prosleđen `currentStatus`, proverava matricu tranzicija.
 * @returns {Promise<object>}
 */
export async function updateObligationStatus(obligationId, status, comment = '', options = {}) {
  const user = await getAuthUser();
  assertCanUpdateObligationStatus(user);
  if (options.currentStatus != null && String(options.currentStatus).trim() !== '') {
    assertValidObligationStatusTransition(options.currentStatus, status, {
      actionPlanStatus: options.actionPlanStatus,
    });
  }
  const payload = {
    obligation_id: obligationId,
    status,
  };
  if (comment && String(comment).trim()) {
    payload.status_change_comment = String(comment).trim();
  }
  const response = await ApiService.post(OBLIGATION.STATUS_UPDATE, payload);
  return response.data;
}

/**
 * Šalje obavezu u sledeću fazu (PROPOSAL → CREATED, ALIGNMENT → UNDER_REVIEW, …).
 * @param {string|number} obligationId - ID obaveze.
 * @param {'PROPOSAL'|'ALIGNMENT'|'PLANNING'} phase - Faza za submit.
 * @returns {Promise<object>}
 */
export async function submitObligationPhase(obligationId, phase) {
  const map = {
    PROPOSAL: 'CREATED',
    ALIGNMENT: 'UNDER_REVIEW',
    PLANNING: 'ACCEPTED',
  };
  return updateObligationStatus(obligationId, map[phase] || 'CREATED');
}

/**
 * Ponovo otvara fazu obaveze (isti status map kao submitObligationPhase).
 * @param {string|number} obligationId - ID obaveze.
 * @param {'PROPOSAL'|'ALIGNMENT'|'PLANNING'} phase - Faza za reopen.
 * @returns {Promise<object>}
 */
export async function reopenObligationPhase(obligationId, phase) {
  const map = {
    PROPOSAL: 'CREATED',
    ALIGNMENT: 'UNDER_REVIEW',
    PLANNING: 'ACCEPTED',
  };
  return updateObligationStatus(obligationId, map[phase] || 'CREATED');
}

/**
 * Fetch related organizations for an obligation.
 * @param {string} obligationId
 * @returns {Promise<{ organizations: Array }>}
 */
export async function fetchRelatedOrganizations(obligationId) {
  const response = await ApiService.post(OBLIGATION.RELATED_ORGANIZATION_SEARCH, {
    obligation_id: obligationId,
  });
  return response.data;
}

/**
 * Add a related organization to an obligation.
 * @param {string} obligationId
 * @param {string} organizationId
 * @param {boolean} [mainResponsible=false]
 * @returns {Promise<Object>}
 */
export async function addRelatedOrganization(obligationId, organizationId, mainResponsible = false) {
  const response = await ApiService.post(OBLIGATION.RELATED_ORGANIZATION_ADD, {
    obligation_id: obligationId,
    organization_id: organizationId,
    main_responsible: mainResponsible,
  });
  return response.data;
}

/**
 * Remove a related organization from an obligation.
 * @param {string} obligationId
 * @param {string} organizationId
 * @returns {Promise<Object>}
 */
export async function removeRelatedOrganization(obligationId, organizationId) {
  const response = await ApiService.post(OBLIGATION.RELATED_ORGANIZATION_REMOVE, {
    obligation_id: obligationId,
    organization_id: organizationId,
  });
  return response.data;
}

/**
 * Add a moderator to an obligation.
 * @param {string} obligationId
 * @param {string} moderatorAccountId
 * @returns {Promise<Object>}
 */
export async function addModerator(obligationId, moderatorAccountId) {
  const response = await ApiService.post(OBLIGATION.MODERATOR_ADD, {
    moderator_account_id: moderatorAccountId,
    obligation_id: obligationId,
  });
  return response.data;
}

/**
 * Remove a moderator from an obligation.
 * @param {string} obligationId
 * @param {string} moderatorAccountId
 * @returns {Promise<Object>}
 */
export async function removeModerator(obligationId, moderatorAccountId) {
  const response = await ApiService.post(OBLIGATION.MODERATOR_REMOVE, {
    moderator_account_id: moderatorAccountId,
    obligation_id: obligationId,
  });
  return response.data;
}

/**
 * Re-export ObligationActivityService funkcija (legacy import put u admin feature-u).
 * @see @/shared/services/ObligationActivityService
 */
export {
  searchObligationActivities,
  saveOrUpdateObligationActivity,
  archiveObligationActivity,
  reorderObligationActivities,
  pickLatestObligationImplementation,
  searchObligationImplementations,
  saveOrUpdateObligationImplementation,
} from '@/shared/services/ObligationActivityService';
